import { useState } from 'react'
import { UseFormReturn } from 'react-hook-form'

import { F0DatePicker } from '@factorialco/f0-react'
import { DatePickerValue } from '@factorialco/f0-react/dist/experimental'
import { ShiftManagementCoverageSectionDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { i18nDateToString } from 'modules/core/lib/dates'

import { useDebounceQuery } from 'modules/shift_management/hooks/useDebouncedQuery'
import { DAY_IN_MS } from 'modules/shift_management/lib/consts/ShiftManagementFormats'
import { buildDateRange } from 'modules/shift_management/lib/dateFormatter'
import { SchemaType } from 'modules/shift_management/lib/forms/GenerateCoverageForm'

interface Props {
  targetStartAt: Date
  form: UseFormReturn<SchemaType>
  setAlert: (value: boolean) => void
}

const DateRange = observer(({ targetStartAt, form, setAlert }: Props) => {
  const [localDate, setLocalDate] = useState<Date>(targetStartAt)
  const { onChange } = useDebounceQuery({
    query: ShiftManagementCoverageSectionDocument,
    buildVariables: (date) => {
      const endAt = buildDateRange(date, form.getValues('daysToGenerate') || 0, DAY_IN_MS)
      return {
        startAt: i18nDateToString(date, 'backend'),
        endAt: i18nDateToString(endAt, 'backend'),
        locationId: form.getValues('locationIds')?.[0] ?? undefined,
      }
    },
    onResolve: (result) => {
      const unassignedShiftsLength = result?.unassignedShifts?.length ?? 0
      setAlert(unassignedShiftsLength > 0)
    },
    delay: 0,
    key: 'shiftManagement',
    form,
    fieldName: 'targetStartAt',
  })

  useUnsafeEffect(() => {
    onChange(targetStartAt)
  }, [form.getValues('locationIds')])

  const onSelect = (value: DatePickerValue | undefined) => {
    if (!value?.value) return

    setLocalDate(value.value.from)
    onChange(value.value.from)
  }

  return (
    <F0DatePicker
      label=""
      hideLabel
      onChange={onSelect}
      size="md"
      value={{
        granularity: 'week',
        value: {
          from: localDate,
          to: buildDateRange(localDate, form.getValues('daysToGenerate') || 0, DAY_IN_MS),
        },
      }}
    />
  )
})

export { DateRange }
